import { Form, OptionToggleGroup } from 'components';
import { MediaUpload } from 'components/Form/inputs/MediaUpload/MediaUpload';
import SelectedItems from 'pages/ModulePage/components/SelectedItems/SelectedItems';
import SelectItemsModal from 'pages/ModulePage/components/SelectItemsModal/SelectItemsModal';
import { useFormikContext } from 'formik';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const BANNER_MODES = {
  EXISTING: 'existing',
  CUSTOM: 'custom',
};

const getBannerKey = (banner) => banner.uid;

const filterBannerByName = (item, query) =>
  (item.name || '').toLowerCase().includes(query.toLowerCase());

const getBannerItemCardProps = (banner, { t }) => ({
  imageSrc: banner.image_url,
  imageAlt: banner.name || 'Banner',
  title: banner.name || null,
  meta: [
    {
      label: t('owner'),
      value: typeof banner.owner === 'string' ? banner.owner : 'Brand',
    },
  ],
});

const BannerCustomForm = () => {
  const { t } = useTranslation('ownerPortal', { keyPrefix: 'pageModules' });
  const { values } = useFormikContext();

  return (
    <>
      <Form.GroupVertical required label={t('name')}>
        <Form.Input name="name" type="text" />
      </Form.GroupVertical>
      <Form.GroupVertical required label={t('bannerImage')}>
        <MediaUpload
          name="image"
          maxFiles={1}
          withCrop
          cropMediaType="banner"
          previewData={{ name: values.name }}
        />
      </Form.GroupVertical>
      <Form.GroupVertical required label={t('bannerUrl')}>
        <Form.Input name="url" type="url" placeholder="https://example.com" />
      </Form.GroupVertical>
    </>
  );
};

export const createBannerConfig = (useAvailableItems) => {
  const BannerConfig = ({ isEditMode }) => {
    const { t } = useTranslation('ownerPortal', { keyPrefix: 'pageModules' });
    const { values, setFieldValue } = useFormikContext();
    const { items, isLoading } = useAvailableItems();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [search, setSearch] = useState('');

    const selectedItems = useMemo(() => values.selectedItems || [], [values.selectedItems]);

    const filteredItems = useMemo(() => {
      if (!search.trim()) return items;
      return items.filter((item) => filterBannerByName(item, search.trim()));
    }, [items, search]);

    const toggleOptions = useMemo(
      () => [
        {
          value: BANNER_MODES.EXISTING,
          title: t('bannerSourceExisting'),
          description: t('bannerSourceExistingDescription'),
        },
        {
          value: BANNER_MODES.CUSTOM,
          title: t('bannerSourceCustom'),
          description: t('bannerSourceCustomDescription'),
        },
      ],
      [t],
    );

    const handleModeChange = (mode) => {
      setFieldValue('mode', mode);

      if (mode === BANNER_MODES.EXISTING) {
        setFieldValue('name', '');
        setFieldValue('image', []);
        setFieldValue('existing_image_id', null);
        setFieldValue('existing_image_url', null);
        setFieldValue('url', '');
        return;
      }

      setFieldValue('selectedItems', []);
    };

    return (
      <>
        {!isEditMode && (
          <OptionToggleGroup
            value={values.mode}
            onChange={handleModeChange}
            options={toggleOptions}
          />
        )}

        <div
          style={
            isEditMode
              ? undefined
              : { marginTop: 32, paddingTop: 24, borderTop: '1px solid #e9ecef' }
          }
        >
          {values.mode === BANNER_MODES.EXISTING ? (
            <>
              <Form.GroupHorizontal label={t('selectedItem')}>
                <SelectedItems
                  isLoading={isLoading}
                  items={selectedItems}
                  setItems={(next) => setFieldValue('selectedItems', next)}
                  getItemKey={getBannerKey}
                  isMultiple={false}
                  getItemCardProps={getBannerItemCardProps}
                  onOpenModal={() => setIsModalOpen(true)}
                />
              </Form.GroupHorizontal>
              <SelectItemsModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSelect={(next) => setFieldValue('selectedItems', next)}
                initialSelected={selectedItems}
                items={filteredItems}
                isLoading={isLoading}
                getItemKey={getBannerKey}
                isMultiple={false}
                getItemCardProps={getBannerItemCardProps}
                onSearch={setSearch}
              />
            </>
          ) : (
            <BannerCustomForm />
          )}
        </div>
      </>
    );
  };

  return BannerConfig;
};
